import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { PricingComponents, RateBreakdown } from './api/pricing.types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('en-EU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('en-EU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

type RateBreakdownSource = {
  rateBreakdown?: RateBreakdown;
  components?: PricingComponents;
} | null | undefined;

export function normalizeRateBreakdown(source: RateBreakdownSource): RateBreakdown | null {
  if (!source) return null;
  if (source.rateBreakdown) {
    return source.rateBreakdown;
  }

  if (!source.components) {
    return null;
  }

  return {
    baseRate: Number((source.components.baseRate || 0) * 100),
    spreadBps: Math.round((source.components.creditSpread || 0) * 10000),
    liquidityPremiumBps: Math.round((source.components.liquidityPremium || 0) * 10000),
    capitalChargeBps: Math.round((source.components.capitalCharge || 0) * 10000),
    totalRatePercent: Number((source.components.totalRate || 0) * 100),
  };
}

export function getAllInRatePercent(source: RateBreakdownSource): number | null {
  const breakdown = normalizeRateBreakdown(source);
  return breakdown ? breakdown.totalRatePercent : null;
}

export function maskPII(value: string | null | undefined): string {
  if (!value) return '—';
  if (value.length <= 4) return '****';
  return '****' + value.slice(-4);
}
